import { useEffect, useState } from 'react';
import api from '../../services/api';

/**
 * Markdown `![alt](imageId)` in a KB article body stores a raw image id, not a
 * URL — the API is Bearer-token authenticated (no cookies), so a plain <img src>
 * can't attach the token. This fetches the image as a blob via the authenticated
 * axios instance and renders it as an object URL instead.
 */
export default function KbImage({ articleId, imageId, alt }: { articleId: string; imageId: string; alt?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/kb/${articleId}/images/${imageId}`, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [articleId, imageId]);

  if (failed) return <span className="text-xs text-red-500">[Image failed to load]</span>;
  if (!src) return <span className="text-xs text-gray-400 inline-block py-2">Loading image…</span>;
  return <img src={src} alt={alt || 'Screenshot'} className="rounded-lg border border-gray-200 max-w-full my-2" />;
}
